import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type { OrganizationDomainListItem } from '../types/models/OrganizationDomain';
import type { ApiResponseOrganizationDomainList } from '../types/api/ApiResponseOrganizationDomain';

interface UseOrganizationDomainsReturn {
  organizationDomains: OrganizationDomainListItem[];
  loading: boolean;
  error: string | null;
}

// `enabled` (default true) defers the fetch: a consumer that only needs the domains conditionally —
// e.g. a modal passing `isOpen` — avoids hitting /organization/domains until it actually opens, and
// refetches each time it re-enables.
export const useOrganizationDomains = (enabled = true): UseOrganizationDomainsReturn => {
  const [organizationDomains, setOrganizationDomains] = useState<OrganizationDomainListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const auth = useAuth();
  const apiFetch = useApiFetch();

  const fetchOrganizationDomains = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiFetch('/organization/domains');

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = (await response.json()) as ApiResponseOrganizationDomainList;
      if (data.status === 'success' && data.data?.domains) {
        setOrganizationDomains(data.data.domains);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while fetching organization domains');
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => {
    if (auth.isAuthenticated && enabled) {
      fetchOrganizationDomains();
    }
  }, [auth.isAuthenticated, enabled, fetchOrganizationDomains]);

  return { organizationDomains, loading, error };
};
